import { useState } from "react";
import { toast } from "sonner";
import { BookmarkSimple, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export async function addToCookbook(payload, items) {
  const { data } = await api.post("/search/add-to-cookbook", {
    ...payload,
    recipe_ids: items.filter((i) => i.source !== "historical").map((i) => i.id),
    historical_ids: items.filter((i) => i.source === "historical").map((i) => i.id),
  });
  return data;
}

export const notifyAdded = (data) => {
  toast.success(`Added ${data.saved} to “${data.title}”`);
  if (data.limit_hit) toast.warning(data.limit_hit);
};

export const AddToCookbookButton = ({ item }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [books, setBooks] = useState(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/cookbooks");
      const list = Array.isArray(data) ? data : [...(data.owned || []), ...(data.co_edited || [])];
      setBooks(list);
    } catch { setBooks([]); }
  };

  const add = async (payload) => {
    setBusy(true);
    try {
      const data = await addToCookbook(payload, [item]);
      notifyAdded(data);
      setOpen(false); setNewName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to cookbook");
    } finally { setBusy(false); }
  };

  if (!user) return null;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && books === null) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`search-add-cb-${item.id}`}
          aria-label={`Add ${item.title} to a cookbook`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-rf-ink/15 bg-white px-2.5 py-1 text-xs font-bold text-rf-ink-soft hover:border-rf-orange hover:text-rf-orange transition-colors"
        >
          <Plus size={12} weight="bold" /> Cookbook
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2 bg-rf-cream" data-testid="search-add-cb-menu">
        <p className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-wider font-black text-rf-ink-soft">Copy into…</p>
        <div className="max-h-56 overflow-y-auto">
          {books === null ? (
            <p className="px-2 py-1 text-sm text-rf-ink-soft">Loading…</p>
          ) : books.length === 0 ? (
            <p className="px-2 py-1 text-sm text-rf-ink-soft">No cookbooks yet.</p>
          ) : books.map((c) => (
            <button
              key={c.cookbook_id} type="button" disabled={busy}
              onClick={() => add({ cookbook_id: c.cookbook_id })}
              data-testid={`search-add-cb-opt-${c.cookbook_id}`}
              className="w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-rf-ink hover:bg-white disabled:opacity-50"
            >
              <BookmarkSimple size={14} className="text-rf-orange shrink-0" />
              <span className="truncate">{c.title}</span>
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (newName.trim()) add({ cookbook_name: newName.trim(), create: true }); }}
          className="flex gap-1.5 border-t border-rf-ink/10 mt-2 pt-2"
        >
          <input
            value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={80}
            placeholder="New cookbook name" aria-label="New cookbook name"
            className="input-rf !py-1.5 !px-2 text-sm flex-1 min-w-0" data-testid="search-add-cb-new-name"
          />
          <button type="submit" disabled={busy || !newName.trim()} className="btn-orange !py-1.5 !px-3 text-xs disabled:opacity-40" data-testid="search-add-cb-new-submit">Add</button>
        </form>
      </PopoverContent>
    </Popover>
  );
};
